import cx from 'classnames'
import { useConfirm } from 'components/ConfirmDialog'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import ConfirmSheet from 'components/ConfirmSheet'
import FileButton from 'components/FileButton'
import {
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
} from 'components/ModalSheet'
import DocumentPicker from 'documents/views/DocumentPicker'
import { useObservableFocus } from 'hooks/focus-hooks'
import { useDropzone } from 'hooks/use-dropzone'
import { observer } from 'mobx-react'
import * as React from 'react'
import { NEVER, Observable } from 'rxjs'
import styles from './DocumentRequests.sass'
import DocumentRequestsViewModel, {
  RequestedDocumentViewModel,
} from './DocumentRequestsViewModel'

interface DocumentRequestsProps {
  vm: DocumentRequestsViewModel
  disabled?: boolean
  readOnly?: boolean
  focus$?: Observable<unknown>
  onFocus?: () => unknown
}

export default observer(function DocumentRequests({
  vm,
  onFocus,
  disabled,
  focus$ = NEVER,
  readOnly = false,
}: DocumentRequestsProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  // Focus the first file button or anchor element we can find.
  const focus = React.useCallback(
    () =>
      (
        (rootRef.current?.querySelector('input[type=file]') as
          | HTMLInputElement
          | undefined) || rootRef.current?.querySelector('a')
      )?.focus(),
    []
  )
  useObservableFocus(focus$, focus)

  const receiveFiles = React.useCallback(
    (fileList: FileList) => {
      const files = Array.from(fileList)

      files.forEach(vm.uploadSupplemental)
    },
    [vm]
  )

  return (
    <div className={cx(styles.root)} ref={rootRef}>
      {vm.documentRequests.map((req) => (
        <DocumentRequestItem
          onFocus={onFocus}
          key={req.id}
          documentRequest={req}
          disabled={disabled}
          readOnly={readOnly}
          data-test={`document-request-item-${req.id}`}
        />
      ))}
      <AdditionalDocumentsUploader
        onChange={receiveFiles}
        disabled={disabled}
      />
    </div>
  )
})

interface AdditionalDocumentsUploaderProps {
  disabled?: boolean
  onFocus?: () => unknown
  onChange: (files: FileList) => void
}

const AdditionalDocumentsUploader = observer(
  function AdditionalDocumentsUploader({
    disabled,
    onFocus,
    onChange,
  }: AdditionalDocumentsUploaderProps) {
    const [dndState, dropzoneRef] = useDropzone<HTMLDivElement>(onChange)

    return (
      <div
        className={cx(
          styles.item,
          styles.small,
          styles.center,
          dndState.isOver && styles.dragOver
        )}
        ref={dropzoneRef}
      >
        {dndState.isOver ? (
          'Drop file to upload'
        ) : (
          <>
            <div className={styles.smallText}>Drag and drop files here or</div>
            <div className={styles.itemActions}>
              <FileButton
                multiple={true}
                disabled={disabled}
                onChange={disabled ? undefined : onChange}
              >
                <ItemAction withoutPipe disabled={disabled} onFocus={onFocus}>
                  Upload Another File
                </ItemAction>
              </FileButton>
            </div>
          </>
        )}
      </div>
    )
  }
)

interface DocumentRequestItemProps {
  documentRequest: RequestedDocumentViewModel
  disabled?: boolean
  onFocus?: () => unknown
  ['data-test']?: string
  readOnly: boolean
}

const DocumentRequestItem = observer(function DocumentRequestItem({
  documentRequest,
  onFocus,
  disabled,
  'data-test': dataTest,
  readOnly = false,
}: DocumentRequestItemProps) {
  // Only allow dropping files when there are no existing docs.
  const canReceiveFiles = !documentRequest.documentId
  const receiveFiles = React.useCallback(
    (items: FileList) =>
      canReceiveFiles &&
      items &&
      items.length >= 1 &&
      documentRequest.newDocument(Array.from(items)[0]),
    [documentRequest, canReceiveFiles]
  )
  const confirmRemovalState = useConfirm()
  const onRemoveClick = React.useCallback(
    () => confirmRemovalState.show(documentRequest.clear),
    [confirmRemovalState, documentRequest]
  )

  const onDownloadClick = React.useCallback(documentRequest.download, [
    documentRequest,
  ])

  const [dndState, dropzoneRef] = useDropzone<HTMLDivElement>(receiveFiles)
  const uploadPercent =
    documentRequest.document?.uploadProgress?.percent ?? null

  return (
    <div
      className={cx(
        styles.item,
        dndState.isOver && canReceiveFiles && styles.dragOver
      )}
      ref={dropzoneRef}
    >
      <div className={styles.itemContent}>
        <p className={styles.itemTitle}>{documentRequest.title}</p>
        {uploadPercent !== null ? (
          <UploadProgress percent={uploadPercent} />
        ) : (
          <p
            className={cx(
              documentRequest.document ? styles.documentName : styles.help
            )}
            data-test={`${dataTest}-filename`}
          >
            {documentRequest.document
              ? documentRequest.document.latestVersion?.files[0]?.fileName
              : dndState.isOver
              ? 'Drop file to upload'
              : !readOnly
              ? 'Drag and drop your file here'
              : ''}
          </p>
        )}
      </div>
      {uploadPercent === null && (
        <div className={styles.itemExtra}>
          {documentRequest.document ? (
            documentRequest.document.uploadProgress ? null : (
              <div className={styles.itemActions}>
                <ItemAction
                  disabled={disabled && !readOnly}
                  onFocus={onFocus}
                  onClick={readOnly ? onDownloadClick : onRemoveClick}
                  data-test={`${dataTest}-remove`}
                >
                  {readOnly ? 'Download' : 'Remove'}
                </ItemAction>
              </div>
            )
          ) : (
            !readOnly && (
              <div className={styles.itemActions}>
                <FileButton
                  disabled={disabled}
                  onChange={disabled ? undefined : receiveFiles}
                  data-test={`${dataTest}-file-input`}
                >
                  <ItemAction disabled={disabled} onFocus={onFocus}>
                    Upload
                  </ItemAction>
                </FileButton>
                <ItemAction
                  onClick={documentRequest.documentPickerViewModel.show}
                  onFocus={onFocus}
                  disabled={disabled}
                  data-test={`${dataTest}-link`}
                >
                  Link
                </ItemAction>
              </div>
            )
          )}
        </div>
      )}
      <DocumentPicker
        data-test="link-docs"
        vm={documentRequest.documentPickerViewModel}
      />
      <ConfirmRemovalSheet state={confirmRemovalState} />
    </div>
  )
})

interface ItemActionProps {
  children: React.ReactNode
  disabled?: boolean
  onClick?: () => unknown
  onFocus?: () => unknown
  withoutPipe?: boolean
  buttonRef?: React.MutableRefObject<HTMLAnchorElement | null>
  ['data-test']?: string
}

function ItemAction({
  children,
  onClick,
  onFocus,
  buttonRef,
  disabled,
  withoutPipe,
  'data-test': dataTest,
}: ItemActionProps) {
  const click = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      onClick?.()
    },
    [onClick]
  )
  return (
    <a
      className={cx(
        styles.itemAction,
        disabled && styles.disabled,
        withoutPipe && styles.withoutPipe
      )}
      href="#"
      onClick={disabled ? undefined : click}
      ref={buttonRef}
      onFocus={onFocus}
      aria-disabled={disabled}
      data-test={dataTest}
    >
      {children}
    </a>
  )
}

interface ConfirmRemovalSheetProps {
  state: ConfirmViewModel
}

/**
 " Confirm Removal Sheet
 */
const ConfirmRemovalSheet = observer(function ConfirmRemovalSheet({
  state,
}: ConfirmRemovalSheetProps) {
  return (
    <ConfirmSheet
      state={state}
      danger
      yes="Remove"
      data-test={{
        confirm: 'remove-document-confirm',
        decline: 'remove-document-decline',
      }}
    >
      <ModalSheetContentBlock>
        <ModalSheetContentTitle>Are you sure?</ModalSheetContentTitle>
        <ModalSheetContentDescription>
          The document will be removed from the slot but will remain linked.
        </ModalSheetContentDescription>
      </ModalSheetContentBlock>
    </ConfirmSheet>
  )
})

interface UploadProgressProps {
  percent: number
}

/**
 " Upload Progress
 */
export function UploadProgress({ percent }: UploadProgressProps) {
  return (
    <div className={styles.uploadProgressContainer}>
      <div className={styles.uploadProgressTrack}>
        <div
          className={styles.uploadProgressBar}
          style={{
            width: `${percent}%`,
          }}
        />
      </div>
    </div>
  )
}
